import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Box,
  TextField,
  Typography,
  Card,
  CardContent,
  Avatar,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, MoreVert } from "@material-ui/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import Title from "../../ui/Title";
import FloatingActions from "../../ui/FloatingActions";
import Confirm from "../../ui/Confirm";

import { confirm as confirmMsgs, common } from "../../messages";
import { MANAGER_ROUTES } from "../../utils/constants";
import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";

const useStyles = makeStyles((theme) => ({
  primaryClamp2: {
    fontWeight: 500,
  },
  card: {
    marginTop: theme.spacing(2),
  },
  listItem: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    alignItems: "flex-start",
  },
  avatar: {
    marginRight: theme.spacing(2),
    backgroundColor: "#ccc",
  },
  secondaryBlock: {
    display: "flex",
    flexDirection: "column",
    marginTop: 4,
  },
  categories: {
    fontSize: 12,
    color: "#666",
  },
}));

export default function ManageProfessionals() {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const { bmapi, notifyError, notifySuccess } = useBmapi();

  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [list, setList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(false);

  const getInitials = (p) =>
    `${(p.first_name || "").charAt(0)}${(p.last_name || "").charAt(
      0
    )}`.toUpperCase();

  const getCategoryLabels = (ids) => {
    if (!ids || !ids.length) return "";
    return categoriesOptions
      .filter((c) => ids.includes(c.value))
      .map((c) => c.label)
      .join(", ");
  };

  // LOAD professionals
  const loadList = useCallback(() => {
    if (!bmapi) return;

    bmapi
      .getProfessionals()
      .then((resp) => {
        const sorted = [...(resp || [])].sort((a, b) =>
          (a.last_name || "").localeCompare(
            b.last_name || "",
            intl.locale || undefined,
            { sensitivity: "base" }
          )
        );

        setList(sorted);
        setFilteredList(sorted);
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, intl, notifyError]);

  // LOAD categories
  useEffect(() => {
    if (!bmapi) return;

    bmapi
      .getGenListCategoriesProfessional()
      .then((resp) => {
        const options =
          resp?.items?.map((i) => ({
            value: i.id,
            label: i.str_value,
          })) || [];
        setCategoriesOptions(options);
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, notifyError, intl]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // FILTER
  useEffect(() => {
    let filtered = list;

    if (query && query.length >= 2) {
      const q = query.toLowerCase();
      filtered = filtered.filter((p) =>
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(q)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter((p) =>
        (p.categories || []).includes(selectedCategory)
      );
    }

    setFilteredList(filtered);
  }, [query, selectedCategory, list]);

  const handleAdd = () => history.push(MANAGER_ROUTES.PROFESSIONAL);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleEdit = () => {
    history.push(`${MANAGER_ROUTES.PROFESSIONAL}?id=${selectedId}`);
    handleMenuClose();
  };

  const handleDuplicate = () => {
    history.push(`${MANAGER_ROUTES.PROFESSIONAL}?cloneId=${selectedId}`);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteRequest(true);
    handleMenuClose();
  };

  const onDelete = async () => {
    if (!selectedId) return;

    try {
      await bmapi.deleteProfessional(selectedId);
      notifySuccess(intl.formatMessage(common.deleteElement));
      setDeleteRequest(false);
      setSelectedId(null);
      loadList();
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    }
  };

  return (
    <Container maxWidth="md">
      <Title>
        <FormattedMessage
          id="navigation.manager.professionals"
          defaultMessage="Professionisti"
        />
      </Title>

      {/* FILTER */}
      <Box mt={2}>
        <Card>
          <CardContent>
            <Box display="flex" flexDirection="column" gap={16}>
              <TextField
                label="Cerca per nome o cognome"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                fullWidth
              />

              <TextField
                select
                label="Categoria"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                fullWidth
              >
                <MenuItem value="">Tutte</MenuItem>
                {categoriesOptions.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* LIST */}
      <Box className={classes.card}>
        <Card>
          <CardContent style={{ padding: 0 }}>
            {!filteredList.length ? (
              <Box p={2}>
                <Typography>Nessun professionista trovato</Typography>
              </Box>
            ) : (
              <List>
                {filteredList.map((p, idx) => (
                  <React.Fragment key={p.id}>
                    {idx > 0 && <Divider />}
                    <ListItem className={classes.listItem}>
                      <Avatar
                        src={p.image_url || ""}
                        className={classes.avatar}
                      >
                        {!p.image_url && getInitials(p)}
                      </Avatar>

                      <ListItemText
                        primary={`${p.first_name} ${p.last_name}`}
                        secondaryTypographyProps={{ component: "div" }} // ✅ FIX WARNING
                        secondary={
                          <Box className={classes.secondaryBlock}>
                            <Typography variant="caption" color="textSecondary">
                              {p.created_at
                                ? intl.formatDate(new Date(p.created_at))
                                : ""}
                            </Typography>

                            {getCategoryLabels(p.categories) && (
                              <Typography
                                component="span"
                                className={classes.categories}
                              >
                                {getCategoryLabels(p.categories)}
                              </Typography>
                            )}
                          </Box>
                        }
                      />

                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={(e) => handleMenuOpen(e, p.id)}
                        >
                          <MoreVert />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>Modifica</MenuItem>
        <MenuItem onClick={handleDuplicate}>Duplica</MenuItem>
        <MenuItem onClick={handleDeleteClick}>Elimina</MenuItem>
      </Menu>

      {/* DELETE */}
      <Confirm
        open={!!deleteRequest}
        onConfirm={onDelete}
        onCancel={() => setDeleteRequest(false)}
        text={intl.formatMessage(confirmMsgs.deleteElement)}
      />

      {/* FAB */}
      <FloatingActions>
        <Fab variant="extended" color="primary" onClick={handleAdd}>
          <Add />
          {intl.formatMessage(common.add)}
        </Fab>
      </FloatingActions>
    </Container>
  );
}
